import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NAV_ITEMS, ROUTES } from '../../constants';
import { FOLLOW_GRAPH_UPDATED_EVENT, authApi } from '../../services/authApi';
import streamApi from '../../services/streamApi';
import { readStoredUser } from '../../utils/navbarUser';
import { hasDirectAdminAccess } from '../../utils/adminAccess';
import { ReactComponent as HomeIcon } from '../../assets/icons/home.svg';
import { ReactComponent as GoLiveIcon } from '../../assets/icons/golive.svg';
import { ReactComponent as GoLiveOvalIcon } from '../../assets/icons/golive-oval.svg';
import { ReactComponent as MessageIcon } from '../../assets/icons/message.svg';
import { ReactComponent as MessageActiveIcon } from '../../assets/icons/message-active.svg';
import { ReactComponent as CommunityIcon } from '../../assets/icons/community.svg';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import { ReactComponent as SubscriptionsIcon } from '../../assets/icons/nav-subscriptions.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as DownloadText } from '../../assets/icons/download-text.svg';
import './LeftSidebar.css';

/**
 * Number of streamers to show before "Show More" is clicked.
 * Keeps the sidebar scannable without overwhelming the user.
 */
const INITIAL_VISIBLE_COUNT = 5;

const LIVE_STATUS_POLL_MS = 30000;

const SIDEBAR_AVATAR_FALLBACKS = {
  spaced: 'https://i.pravatar.cc/150?u=spaced',
  imhere: 'https://i.pravatar.cc/150?u=imhere',
  'not today': 'https://i.pravatar.cc/150?u=nottoday',
  bert: 'https://i.pravatar.cc/150?u=bert',
  void: 'https://i.pravatar.cc/150?u=void',
};

const formatSupporterMeta = (count) => {
  if (typeof count !== 'number' || Number.isNaN(count) || count <= 0) {
    return null;
  }

  if (count >= 1000) {
    const compact = count >= 10000
      ? `${Math.round(count / 1000)}K`
      : `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return `${compact} supporters`;
  }

  return `${count} ${count === 1 ? 'supporter' : 'supporters'}`;
};

const formatViewerMeta = (count) => {
  if (typeof count !== 'number' || Number.isNaN(count) || count <= 0) {
    return null;
  }

  if (count >= 1000) {
    const compact = count >= 10000
      ? `${Math.round(count / 1000)}K`
      : `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return `${compact} viewers`;
  }

  return `${count} viewers`;
};

const ICON_MAP = {
  home: <HomeIcon />,
  broadcast: <GoLiveIcon />,
  chat: <MessageIcon />,
  community: <CommunityIcon />,
  upload: <UploadIcon />,
  star: <SubscriptionsIcon />,
  download: <DownloadIcon viewBox="4 4 25 27" />,
  shield: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
};

/**
 * Left sidebar navigation component.
 *
 * Contains:
 * - Main navigation menu (Home, Go Live!, etc.)
 * - Following section (streamers the user follows)
 * - Currently Live section (all streamers live right now across the platform)
 *
 * Each section is collapsible via "Show More" functionality.
 *
 * @returns {JSX.Element}
 */
function LeftSidebar({ forceDocumentNavigation = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [followingExpanded, setFollowingExpanded] = useState(false);
  const [platformExpanded, setPlatformExpanded] = useState(false);
  const [liveExpanded, setLiveExpanded] = useState(false);
  const [isAdmin] = useState(() => hasDirectAdminAccess());
  const [followingStreamers, setFollowingStreamers] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [currentlyLiveStreamers, setCurrentlyLiveStreamers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadSidebar = async () => {
      const storedUser = readStoredUser();
      const currentUsername = (storedUser?.username || '').trim();

      try {
        const [followingData, liveStreams, featuredCreators] = await Promise.all([
          currentUsername
            ? authApi.getFollowingList(currentUsername).catch(() => ({ following: [] }))
            : Promise.resolve({ following: [] }),
          streamApi.getLiveStreams().catch(() => []),
          streamApi.getFeaturedCreators(20).catch(() => []),
        ]);

        if (cancelled) return;

        const rawFollowing = Array.isArray(followingData?.following)
          ? followingData.following
          : [];

        const followingWithCounts = await Promise.all(
          rawFollowing.map(async (entry) => {
            const count = entry.followersCount != null
              ? entry.followersCount
              : await authApi.getFollowersCount(entry.username)
                  .then((r) => r?.followersCount ?? null)
                  .catch(() => null);
            return {
              id: entry.id,
              name: entry.username,
              avatar: entry.profileImage || null,
              isPro: Boolean(entry.isPro),
              meta: formatSupporterMeta(count) || '',
            };
          })
        );

        if (cancelled) return;
        const following = followingWithCounts;

        const platform = Array.isArray(featuredCreators)
          ? featuredCreators.map((creator) => ({
              id: creator.id,
              name: creator.username,
              avatar: creator.profileImage || creator.profilePictureUrl || creator.oauthProfileImage || null,
              isPro: creator.isPro !== undefined ? Boolean(creator.isPro) : true,
              meta: 'Platform',
            }))
          : [];

        const live = Array.isArray(liveStreams)
          ? liveStreams.map((stream) => ({
              id: stream.userId,
              name: stream.username,
              avatar: stream.avatarUrl || null,
              meta: formatViewerMeta(stream.viewerCount) || 'Live now',
            }))
          : [];

        setFollowingStreamers(following);
        setPlatformUsers(platform);
        setCurrentlyLiveStreamers(live);
      } catch (error) {
        console.error('Failed to load sidebar data:', error);
        if (!cancelled) {
          setFollowingStreamers([]);
          setPlatformUsers([]);
          setCurrentlyLiveStreamers([]);
        }
      }
    };

    loadSidebar();

    const handleFollowGraphUpdated = () => {
      loadSidebar();
    };

    window.addEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    const intervalId = setInterval(loadSidebar, LIVE_STATUS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    };
  }, [location.pathname]);

  const navItems = isAdmin
    ? [...NAV_ITEMS, { id: 'admin', label: 'Admin', icon: 'shield', path: ROUTES.ADMIN }]
    : NAV_ITEMS;

  const visibleFollowing = followingExpanded
    ? followingStreamers
    : followingStreamers.slice(0, INITIAL_VISIBLE_COUNT);

  const visiblePlatform = platformExpanded
    ? platformUsers
    : platformUsers.slice(0, INITIAL_VISIBLE_COUNT);

  const visibleLive = liveExpanded
    ? currentlyLiveStreamers
    : currentlyLiveStreamers.slice(0, INITIAL_VISIBLE_COUNT);

  const navigateTo = (path) => {
    if (!path) return;
    if (forceDocumentNavigation) {
      window.location.assign(path);
      return;
    }
    navigate(path);
  };

  const isNavItemActive = (path) => (
    location.pathname === path
    || location.pathname.startsWith(`${path}/`)
  );

  const renderNavItemContent = (item, isActive) => (
    <>
      <span className="left-sidebar__nav-icon" aria-hidden="true">
        {item.icon === 'broadcast' && isActive ? (
          <span className="left-sidebar__golive-active-wrapper">
            <GoLiveOvalIcon className="left-sidebar__golive-oval" />
            <GoLiveIcon className="left-sidebar__golive-icon-inner" />
          </span>
        ) : item.icon === 'chat' && isActive ? (
          <MessageActiveIcon />
        ) : (
          ICON_MAP[item.icon] || '•'
        )}
      </span>
      {item.id === 'download' ? (
        <span className="left-sidebar__nav-label">
          <DownloadText
            viewBox="718 6 170 28"
            width="170"
            height="28"
            className="left-sidebar__download-text"
            aria-label="Download"
          />
        </span>
      ) : (
        <span className="left-sidebar__nav-label">{item.label}</span>
      )}
      {isActive && (
        <span className="left-sidebar__nav-arrow" aria-hidden="true" />
      )}
    </>
  );

  return (
    <aside className="left-sidebar" aria-label="Main navigation">
      {/* Main Navigation Menu */}
      <nav className="left-sidebar__nav">
        {navItems.map((item) => {
          if (forceDocumentNavigation) {
            const isActive = isNavItemActive(item.path);
            return (
              <a
                key={item.id}
                href={item.path}
                className={`left-sidebar__nav-item ${isActive ? 'left-sidebar__nav-item--active' : ''}`}
              >
                {renderNavItemContent(item, isActive)}
              </a>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `left-sidebar__nav-item ${isActive ? 'left-sidebar__nav-item--active' : ''}`
              }
            >
              {({ isActive }) => renderNavItemContent(item, isActive)}
            </NavLink>
          );
        })}
      </nav>

      <hr className="left-sidebar__divider" />

      {/* Following Section */}
      <section className="left-sidebar__section">
        <h2 className="left-sidebar__section-header">Following</h2>
        <ul className="left-sidebar__streamer-list">
          {visibleFollowing.length === 0 && (
            <li className="left-sidebar__empty">No followed streamers yet</li>
          )}
          {visibleFollowing.map((streamer) => (
            <StreamerListItem
              key={streamer.id}
              streamer={streamer}
              onClick={() => navigateTo(`/profile/${encodeURIComponent(streamer.name)}`)}
            />
          ))}
        </ul>
        {followingStreamers.length > INITIAL_VISIBLE_COUNT && (
          <button
            type="button"
            className="left-sidebar__show-more"
            onClick={() => setFollowingExpanded(!followingExpanded)}
            aria-expanded={followingExpanded}
          >
            {followingExpanded ? '▲ Show Less' : '▼ Show More'}
          </button>
        )}
      </section>

      <hr className="left-sidebar__divider" />

      {/* Platform Users Section */}
      <section className="left-sidebar__section">
        <h2 className="left-sidebar__section-header">Platform Users</h2>
        <ul className="left-sidebar__streamer-list">
          {visiblePlatform.length === 0 && (
            <li className="left-sidebar__empty">No platform users yet</li>
          )}
          {visiblePlatform.map((user) => (
            <StreamerListItem
              key={user.id}
              streamer={user}
              onClick={() => navigateTo(`/profile/${encodeURIComponent(user.name)}`)}
            />
          ))}
        </ul>
        {platformUsers.length > INITIAL_VISIBLE_COUNT && (
          <button
            type="button"
            className="left-sidebar__show-more"
            onClick={() => setPlatformExpanded(!platformExpanded)}
            aria-expanded={platformExpanded}
          >
            {platformExpanded ? '▲ Show Less' : '▼ Show More'}
          </button>
        )}
      </section>

      <hr className="left-sidebar__divider" />

      {/* Currently Live Section */}
      <section className="left-sidebar__section">
        <h2 className="left-sidebar__section-header">Currently Live</h2>
        <ul className="left-sidebar__streamer-list">
          {visibleLive.length === 0 && (
            <li className="left-sidebar__empty">No streamers are live right now</li>
          )}
          {visibleLive.map((streamer) => (
            <StreamerListItem
              key={streamer.id}
              streamer={streamer}
              isLive
              onClick={() => navigateTo(`/profile/${encodeURIComponent(streamer.name)}`)}
            />
          ))}
        </ul>
        {currentlyLiveStreamers.length > INITIAL_VISIBLE_COUNT && (
          <button
            type="button"
            className="left-sidebar__show-more"
            onClick={() => setLiveExpanded(!liveExpanded)}
            aria-expanded={liveExpanded}
          >
            {liveExpanded ? '▲ Show Less' : '▼ Show More'}
          </button>
        )}
      </section>
    </aside>
  );
}

LeftSidebar.propTypes = {
  forceDocumentNavigation: PropTypes.bool,
};

/**
 * Individual streamer item in the sidebar list.
 *
 * Displays avatar, name, and either follower count or viewer count.
 * Live streamers show a red "LIVE" indicator.
 *
 * @param {Object} props
 * @param {Object} props.streamer - Streamer data object
 * @param {boolean} [props.showFollowers] - Show follower count
 * @param {boolean} [props.showViewers] - Show viewer count
 * @param {boolean} [props.isLive] - Show live indicator
 */
function StreamerListItem({ streamer, isLive, onClick }) {
  const avatarUrl = streamer.avatar || SIDEBAR_AVATAR_FALLBACKS[(streamer.name || '').toLowerCase()] || null;

  return (
    <li>
      <button
        type="button"
        className={`left-sidebar__streamer-item ${isLive ? 'left-sidebar__streamer-item--live' : ''}`}
        onClick={onClick}
      >
        <div className={`left-sidebar__streamer-avatar ${isLive ? 'left-sidebar__streamer-avatar--live' : ''}`}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" referrerPolicy="no-referrer" />
          ) : (
            <span className="left-sidebar__avatar-placeholder">
              {streamer.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="left-sidebar__streamer-info">
          <span className="left-sidebar__streamer-name">
            {streamer.name}
            {streamer.isPro && (
              <span className="left-sidebar__pro-badge" title="Discovery Plan PRO">
                PRO
              </span>
            )}
            {isLive && <span className="left-sidebar__live-dot" aria-label="Live" />}
          </span>
          {streamer.meta && (
            <span className={`left-sidebar__streamer-meta ${isLive ? 'left-sidebar__streamer-meta--live' : ''}`}>
              {streamer.meta}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

StreamerListItem.propTypes = {
  streamer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string,
    isPro: PropTypes.bool,
    meta: PropTypes.string,
  }).isRequired,
  isLive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default LeftSidebar;
